/**
 * Token-platform (new-api) 凭证在 localStorage 的统一管理。
 *
 * 安全约束（WIS-496）：这些凭证历史上用全局静态 key 存储，登出/换号不清，
 * 导致同浏览器后续账号复用上一个账号的 new-api 身份（跨账号串号泄漏）。
 *
 * 现统一在此收口，要求：
 *   1. 登出 / 换号 / session 失效 → 调 clearTokenPlatformCredentials() 清掉；
 *   2. 使用前校验 owner（wischoicer userId）与当前登录用户一致，不一致强制重换。
 *
 * base_url 是全局配置（token 平台前端地址），不属于凭证，不在此清理。
 */
const TOKEN_PLATFORM_TOKEN_KEY = 'wischoicer_token_platform_access_token';
const TOKEN_PLATFORM_USER_ID_KEY = 'wischoicer_token_platform_new_api_user_id';
const TOKEN_PLATFORM_OWNER_KEY = 'wischoicer_token_platform_owner_user_id';

const canUseStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const getTokenPlatformToken = (): string => {
  if (!canUseStorage()) return '';
  return localStorage.getItem(TOKEN_PLATFORM_TOKEN_KEY) || '';
};

export const setTokenPlatformToken = (token: string) => {
  if (!canUseStorage()) return;
  localStorage.setItem(TOKEN_PLATFORM_TOKEN_KEY, token);
};

export const getTokenPlatformUserId = (): string => {
  if (!canUseStorage()) return '';
  return localStorage.getItem(TOKEN_PLATFORM_USER_ID_KEY) || '';
};

export const setTokenPlatformUserId = (userId: string | number | null) => {
  if (!canUseStorage()) return;
  if (userId != null) {
    localStorage.setItem(TOKEN_PLATFORM_USER_ID_KEY, String(userId));
  } else {
    localStorage.removeItem(TOKEN_PLATFORM_USER_ID_KEY);
  }
};

/** 当前凭证归属的 wischoicer userId（写入凭证时一并打戳） */
export const getTokenPlatformOwner = (): string => {
  if (!canUseStorage()) return '';
  return localStorage.getItem(TOKEN_PLATFORM_OWNER_KEY) || '';
};

export const setTokenPlatformOwner = (wischoicerUserId: string | number | null) => {
  if (!canUseStorage()) return;
  if (wischoicerUserId != null && wischoicerUserId !== '') {
    localStorage.setItem(TOKEN_PLATFORM_OWNER_KEY, String(wischoicerUserId));
  } else {
    localStorage.removeItem(TOKEN_PLATFORM_OWNER_KEY);
  }
};

/**
 * 清除 token-platform 凭证（access_token / new_api_user_id / owner）。
 * base_url 属全局配置，保留。登出 / 换号 / session 失效时调用。
 */
export const clearTokenPlatformCredentials = () => {
  if (!canUseStorage()) return;
  localStorage.removeItem(TOKEN_PLATFORM_TOKEN_KEY);
  localStorage.removeItem(TOKEN_PLATFORM_USER_ID_KEY);
  localStorage.removeItem(TOKEN_PLATFORM_OWNER_KEY);
};
